(() => {
  const COLOUR_DRAWER_NAME = "product-colour-drawer";
  const FALLBACK_MOTION_MS = 250;

  class ProductColourDrawer extends HTMLElement {
    #abortController = null;
    #dialog = null;
    #panelPromise = null;
    #opening = false;
    #closeTimer = 0;
    #closeListener = null;
    #closing = false;
    #lastTrigger = null;
    #activeFilter = "all";

    connectedCallback() {
      this.#dialog = this.querySelector("[data-colour-drawer-dialog]");
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      document.addEventListener("click", this.#handleDocumentClick, { signal });
      this.#dialog?.addEventListener("close", this.#handleDialogClose, {
        signal,
      });
      this.#dialog?.addEventListener("cancel", this.#handleDialogCancel, {
        signal,
      });
      this.addEventListener("click", this.#handleInternalClick, { signal });
      this.addEventListener("pointerover", this.#handlePrefetch, { signal });
      this.addEventListener("focusin", this.#handlePrefetch, { signal });
      this.addEventListener("pointerdown", this.#handlePrefetch, { signal });

      this.#syncColourwayHrefs();
      this.#syncFilterVisibility();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#cancelClose();
    }

    /**
     * Same mechanism as the cart drawer (assets/cart-drawer.js): showModal()
     * puts the dialog in the top layer with the panel at its resting,
     * off-screen position; one forced layout later the `is-open` class moves
     * it in and the CSS transition carries it. That class is the whole
     * mechanism. The dialog is overflow: clip, never hidden, so the close
     * button taking focus cannot scroll the panel into view and cancel the
     * motion — see the stylesheet.
     */
    async open(trigger) {
      if (!this.#dialog || this.#dialog.open || this.#opening) return;

      this.#opening = true;
      this.#lastTrigger = trigger instanceof HTMLElement ? trigger : null;
      await this.#ensurePanel();
      this.#opening = false;

      if (!this.isConnected || !this.#dialog || this.#dialog.open) return;
      this.#cancelClose();
      this.#dialog.showModal();

      // Layout has to see the off-screen position once before the class
      // changes it, or there is nothing for the transition to start from.
      this.querySelector(
        ".product-colour-drawer__panel",
      )?.getBoundingClientRect();
      this.#dialog.classList.add("is-open");
    }

    /**
     * Drops the class so the panel slides out and the scrim fades, then closes
     * the dialog once the panel's transition has ended — dialog.close() flips
     * display straight away, so it has to wait for the motion.
     */
    close() {
      if (!this.#dialog?.open || this.#closing) return;

      const dialog = this.#dialog;
      const panel = this.querySelector(".product-colour-drawer__panel");

      const finish = () => {
        this.#cancelClose();
        if (dialog.open) dialog.close();
      };

      this.#closing = true;
      dialog.classList.remove("is-open");

      if (this.#prefersReducedMotion() || !panel) {
        finish();
        return;
      }

      this.#closeListener = (event) => {
        if (event.target === panel && event.propertyName === "transform") {
          finish();
        }
      };
      panel.addEventListener("transitionend", this.#closeListener);

      // A transitionend can go missing (tab in the background, transition cut
      // short by a style change); the timer makes sure the drawer still shuts.
      this.#closeTimer = window.setTimeout(finish, this.#motionMs() + 50);
    }

    #cancelClose() {
      this.#closing = false;

      if (this.#closeTimer) {
        window.clearTimeout(this.#closeTimer);
        this.#closeTimer = 0;
      }

      if (this.#closeListener) {
        this.querySelector(
          ".product-colour-drawer__panel",
        )?.removeEventListener("transitionend", this.#closeListener);
        this.#closeListener = null;
      }
    }

    #prefersReducedMotion() {
      return (
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
      );
    }

    // Reads the header's motion token so the JS timer and the CSS transition
    // cannot drift apart when the token changes.
    #motionMs() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--search-motion-duration")
        .trim();
      const value = Number.parseFloat(raw);

      if (!Number.isFinite(value) || value <= 0) return FALLBACK_MOTION_MS;

      return raw.endsWith("ms") ? value : value * 1000;
    }

    #ensurePanel() {
      if (this.querySelector("[data-colour-drawer-item]")) {
        return Promise.resolve();
      }

      if (this.#panelPromise) return this.#panelPromise;

      const productUrl = this.dataset.productUrl;
      const sectionId = this.dataset.drawerSection || "product-colour-drawer";

      if (!productUrl) return Promise.resolve();

      const url = new URL(productUrl, window.location.origin);
      const variantInput = this.closest(
        "product-form, .product-page__form",
      )?.querySelector('[name="id"]');

      if (variantInput?.value) {
        url.searchParams.set("variant", variantInput.value);
      }

      url.searchParams.set("section_id", sectionId);
      this.setAttribute("aria-busy", "true");

      this.#panelPromise = fetch(url.toString(), { credentials: "same-origin" })
        .then((response) => {
          if (!response.ok) throw new Error("colour drawer");

          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const next = doc.querySelector("[data-colour-drawer-content]");
          const slot = this.querySelector("[data-colour-drawer-content]");

          if (!next || !slot) return;

          slot.replaceWith(next);
          this.#syncColourwayHrefs();
          this.#syncFilterVisibility();
        })
        .catch(() => {
          this.#panelPromise = null;
        })
        .finally(() => {
          this.removeAttribute("aria-busy");
        });

      return this.#panelPromise;
    }

    #syncColourwayHrefs() {
      for (const item of this.querySelectorAll("[data-colour-drawer-item]")) {
        const template = item.querySelector("[data-gallery-preload]");
        const variantUrl = template?.getAttribute("data-colour-variant-url");

        if (variantUrl) item.setAttribute("href", variantUrl);
      }
    }

    #handleDocumentClick = (event) => {
      const openTrigger = event.target.closest("[data-colour-drawer-trigger]");

      if (!openTrigger) return;

      const picker = openTrigger.closest(".product-variant-picker");
      const form = openTrigger.closest("product-form, .product-page__form");

      // Drawer may live inside the picker, or as a sibling within the product form.
      const inPicker = picker?.contains(this);
      const inForm = form?.contains(this);

      if (!inPicker && !inForm) return;

      event.preventDefault();
      this.open(openTrigger);
    };

    #handleDialogClose = () => {
      this.#cancelClose();
      this.#dialog?.classList.remove("is-open");

      if (this.#lastTrigger?.isConnected) {
        this.#lastTrigger.focus();
      }

      this.#lastTrigger = null;
    };

    #handleDialogCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    #handlePrefetch = (event) => {
      const colourItem = event.target.closest("[data-colour-drawer-item]");

      if (!colourItem || !this.contains(colourItem)) return;
      if (colourItem.getAttribute("aria-current") === "true") return;

      if (event.type === "pointerover") {
        const from = event.relatedTarget;

        if (from instanceof Node && colourItem.contains(from)) return;
      }

      const productUrl =
        colourItem
          .querySelector("[data-gallery-preload]")
          ?.getAttribute("data-colour-variant-url") ||
        colourItem.getAttribute("href");

      if (!productUrl) return;

      const swatches = document.querySelector("product-colour-swatches");

      if (swatches && typeof swatches.prefetchColourway === "function") {
        swatches.prefetchColourway(productUrl, colourItem);
      }
    };

    #handleInternalClick = (event) => {
      const closeTrigger = event.target.closest("[data-colour-drawer-close]");

      if (closeTrigger) {
        event.preventDefault();
        this.close();
        return;
      }

      const filterButton = event.target.closest("[data-colour-filter]");

      if (filterButton && this.contains(filterButton)) {
        event.preventDefault();
        this.#setFilter(filterButton.dataset.colourFilter || "all");
        return;
      }

      const colourItem = event.target.closest("[data-colour-drawer-item]");

      if (!colourItem || !this.contains(colourItem)) return;

      if (colourItem.getAttribute("aria-current") === "true") {
        event.preventDefault();
        this.close();
        return;
      }

      const productUrl =
        colourItem
          .querySelector("[data-gallery-preload]")
          ?.getAttribute("data-colour-variant-url") ||
        colourItem.getAttribute("href");

      if (!productUrl) return;

      event.preventDefault();
      this.#selectColourway(productUrl, colourItem);
    };

    async #selectColourway(productUrl, colourItem) {
      this.setAttribute("aria-busy", "true");

      const swatches = document.querySelector("product-colour-swatches");

      try {
        if (swatches && typeof swatches.loadColourway === "function") {
          await swatches.loadColourway(productUrl, {
            pushState: true,
            source: colourItem,
          });
        } else {
          window.location.href = productUrl;
          return;
        }
      } finally {
        this.removeAttribute("aria-busy");
      }

      if (this.isConnected) {
        this.close();
      }
    }

    #setFilter(filterHandle) {
      this.#activeFilter = filterHandle || "all";

      for (const button of this.querySelectorAll("[data-colour-filter]")) {
        const isActive = button.dataset.colourFilter === this.#activeFilter;

        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      }

      for (const item of this.querySelectorAll("[data-colour-drawer-item]")) {
        item.hidden = !this.#itemMatchesFilter(item, this.#activeFilter);
      }
    }

    #itemMatchesFilter(item, filterHandle) {
      if (filterHandle === "all") return true;

      if (filterHandle === "sale") {
        return item.dataset.onSale === "true";
      }

      const handles = (item.dataset.colourFilters || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      return handles.includes(filterHandle);
    }

    #syncFilterVisibility() {
      const items = [...this.querySelectorAll("[data-colour-drawer-item]")];

      for (const button of this.querySelectorAll("[data-colour-filter]")) {
        const handle = button.dataset.colourFilter;

        if (!handle || handle === "all") {
          button.hidden = false;
          continue;
        }

        const hasMatch = items.some((item) =>
          this.#itemMatchesFilter(item, handle),
        );

        button.hidden = !hasMatch;
      }

      this.#setFilter(this.#activeFilter);
    }
  }

  if (!customElements.get(COLOUR_DRAWER_NAME)) {
    customElements.define(COLOUR_DRAWER_NAME, ProductColourDrawer);
  }
})();
